import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import ScrollFadeIn from './../../components/scrollFadeIn';
import SectionBanner from './sectionBanner';
import SectionPageHeader from './sectionPageHeader';

const AXIS_TEXT = 'rgba(0,0,0,0.55)';
const GRID = 'rgba(0,0,0,0.08)';
const AXIS_FONT_SIZE = 7;
// Every chart shares one coordinate system so type sizes stay consistent between them.
// The card is a fixed 389px tall and the svg fills it, so the viewBox ratio is set to
// roughly match the card's inner box once .small-padding is subtracted.
const CHART_VIEWBOX = '0 0 360 230';
const CHART_STYLE = { display: 'block', width: '100%', height: '100%' };
const CHART_CARD_HEIGHT = '389px';
const chartCardStyle = (borderColor) => ({
  border: `2px solid ${borderColor}`,
  borderRadius: '8px',
  overflow: 'hidden',
  height: CHART_CARD_HEIGHT,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Shares by month for the four largest brands; the remainder is the minor tail.
const MAJOR_SERIES = [
  { name: 'McDelivery', color: '#197F9F', values: [30, 28, 34, 34, 35, 35, 32, 31, 32, 32, 30, 31] },
  { name: 'KFC', color: '#F48C5C', values: [28, 30, 30, 33, 31, 31, 34, 33, 32, 31, 29, 30] },
  { name: 'Wagamama', color: '#5FB7AF', values: [7, 7, 5, 4, 2, 2, 4, 6, 10, 11, 15, 12] },
  { name: 'Five Guys', color: '#F7BB49', values: [9, 8, 3, 5, 9, 8, 6, 6, 6, 7, 9, 8] },
];

// The minor tail split back out into individual brands, for the "too many categories" example.
const MINOR_SERIES = [
  { name: 'Nandos', color: '#4CAF50', weight: 0.3 },
  { name: 'Gourmet Burger Kitchen', color: '#8E77FF', weight: 0.18 },
  { name: 'Pizza Express', color: '#75BFF2', weight: 0.14 },
  { name: 'Pepes Piri Piri', color: '#ED5C6D', weight: 0.16 },
  { name: 'Subway', color: '#84E1D9', weight: 0.14 },
  { name: 'Burger King', color: '#CA75DF', weight: 0.08 },
];

// A single hue at three intensities: oldest year lightest, most recent darkest.
const HOURLY_SERIES = [
  {
    name: '2017',
    color: '#a3ccd9',
    values: [49, 77, 39, 37, 24, 22, 10, 9, 7, 11, 25, 33, 45, 56, 46, 44, 40, 46, 42, 40, 37, 38, 45, 46],
  },
  {
    name: '2018',
    color: '#5ea5bc',
    values: [66, 60, 70, 54, 54, 58, 69, 65, 58, 45, 25, 17, 13, 12, 11, 10, 13, 19, 27, 35, 45, 40, 52, 46],
  },
  {
    name: '2019',
    color: '#197F9F',
    values: [26, 23, 14, 25, 38, 64, 84, 100, 80, 96, 100, 96, 94, 78, 78, 77, 70, 78, 79, 73, 60, 45, 35, 24],
  },
];

export default class VisualizationSection extends Component {
  // Stacked share-of-market chart. `grouped` rolls the minor brands into a single
  // neutral "Other" band; otherwise each minor brand gets its own segment.
  renderStackedBarChart(grouped) {
    const left = 28, right = 256, yTop = 12, yBase = 196;
    const ticks = [0, 25, 50, 75, 100];
    const slot = (right - left) / MONTHS.length;
    const barW = 14;
    const yFor = (v) => yBase - (v / 100) * (yBase - yTop);

    const otherTotals = MONTHS.map((_, i) =>
      MAJOR_SERIES.reduce((sum, s) => sum - s.values[i], 100)
    );
    const minorSeries = MINOR_SERIES.map((s, index) => ({
      name: s.name,
      color: s.color,
      values: otherTotals.map((total, i) =>
        index === MINOR_SERIES.length - 1
          ? total - MINOR_SERIES.slice(0, index).reduce((sum, m) => sum + Math.round(total * m.weight), 0)
          : Math.round(total * s.weight)
      ),
    }));
    const series = grouped
      ? [...MAJOR_SERIES, { name: 'Other', color: '#D0D0D0', values: otherTotals }]
      : [...MAJOR_SERIES, ...minorSeries];

    const label = grouped
      ? 'Stacked bar chart with minor brands grouped into a single Other category'
      : 'Stacked bar chart split into many small brand categories';

    return (
      <svg viewBox={CHART_VIEWBOX} width="100%" role="img" aria-label={label} style={CHART_STYLE}>
        {ticks.map((t) => {
          const y = yFor(t);
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke={GRID} strokeWidth="1" />
              <text x={left - 5} y={y + 3} textAnchor="end" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{`${t}%`}</text>
            </g>
          );
        })}
        {MONTHS.map((month, i) => {
          const x = left + i * slot + (slot - barW) / 2;
          let top = yBase;
          return (
            <g key={month}>
              {series.map((s) => {
                const h = (s.values[i] / 100) * (yBase - yTop);
                top -= h;
                return <rect key={s.name} x={x} y={top} width={barW} height={Math.max(h, 0)} fill={s.color} />;
              })}
              <text x={x + barW / 2} y={yBase + 13} textAnchor="middle" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{month}</text>
            </g>
          );
        })}
        <line x1={left} y1={yBase} x2={right} y2={yBase} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
        {series.slice().reverse().map((s, i) => {
          const y = yTop + i * 15;
          return (
            <g key={s.name}>
              <rect x={266} y={y} width={8} height={8} rx="2" fill={s.color} />
              <text x={278} y={y + 7} fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{s.name}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  // Hourly volume by year, drawn in one hue so intensity carries the sequence.
  renderSequentialLineChart() {
    const left = 30, right = 348, yTop = 14, yBase = 186;
    const ticks = [0, 25, 50, 75, 100];
    const points = HOURLY_SERIES[0].values.length;
    const xFor = (i) => left + (i * (right - left)) / (points - 1);
    const yFor = (v) => yBase - (v / 100) * (yBase - yTop);

    return (
      <svg viewBox={CHART_VIEWBOX} width="100%" role="img" aria-label="Line chart using one hue at three intensities to show three years of hourly data" style={CHART_STYLE}>
        {ticks.map((t) => {
          const y = yFor(t);
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke={GRID} strokeWidth="1" />
              <text x={left - 5} y={y + 3} textAnchor="end" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{t}</text>
            </g>
          );
        })}
        {HOURLY_SERIES.map((s) => (
          <path
            key={s.name}
            d={s.values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(v)}`).join(' ')}
            fill="none"
            stroke={s.color}
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        <line x1={left} y1={yBase} x2={right} y2={yBase} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
        {HOURLY_SERIES[0].values.map((_, i) =>
          i % 4 === 0 ? (
            <text key={i} x={xFor(i)} y={yBase + 14} textAnchor="middle" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{`Hour ${i + 1}`}</text>
          ) : null
        )}
        {HOURLY_SERIES.map((s, i) => {
          const x = 128 + i * 46;
          return (
            <g key={s.name}>
              <line x1={x} y1={218} x2={x + 12} y2={218} stroke={s.color} strokeWidth="2.5" strokeLinecap="round" />
              <text x={x + 17} y={221} fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{s.name}</text>
            </g>
          );
        })}
      </svg>
    );
  }

  // Identical data and identical label text in both versions — only the angle of the
  // x-axis labels changes, so the comparison isolates the rotation itself.
  renderAxisAngleChart(diagonal) {
    const values = [18, 24, 21, 29, 26, 33, 31, 38];
    const labels = ["Jan '24", "Feb '24", "Mar '24", "Apr '24", "May '24", "Jun '24", "Jul '24", "Aug '24"];
    const ticks = [0, 10, 20, 30, 40];
    const maxVal = 40;
    const left = 34, right = 348, yTop = 12, yBase = 196;
    const slot = (right - left) / values.length;
    const barW = 24;
    const yFor = (v) => yBase - (v / maxVal) * (yBase - yTop);
    const barPath = (x, y, w, h) => {
      const r = Math.min(4, w / 2, h);
      return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
    };
    const label = diagonal
      ? 'Bar chart with x-axis labels rotated to 45 degrees'
      : 'Bar chart with horizontal x-axis labels';

    return (
      <svg viewBox={CHART_VIEWBOX} width="100%" role="img" aria-label={label} style={CHART_STYLE}>
        {ticks.map((t) => {
          const y = yFor(t);
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke={GRID} strokeWidth="1" />
              <text x={left - 5} y={y + 3} textAnchor="end" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{`$${t}K`}</text>
            </g>
          );
        })}
        {values.map((v, i) => {
          const x = left + i * slot + (slot - barW) / 2;
          const y = yFor(v);
          const cx = x + barW / 2;
          return (
            <g key={labels[i]}>
              <path d={barPath(x, y, barW, yBase - y)} fill="#197f9f" />
              {diagonal ? (
                <text
                  x={cx + 5}
                  y={yBase + 9}
                  textAnchor="end"
                  transform={`rotate(-45 ${cx + 5} ${yBase + 9})`}
                  fontSize={AXIS_FONT_SIZE}
                  fill={AXIS_TEXT}
                  fontFamily="inherit">
                  {labels[i]}
                </text>
              ) : (
                <text x={cx} y={yBase + 13} textAnchor="middle" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{labels[i]}</text>
              )}
            </g>
          );
        })}
        <line x1={left} y1={yBase} x2={right} y2={yBase} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
      </svg>
    );
  }

  renderBarChart(showLeadingZeros) {
    const values = [12, 18, 9, 15, 7];
    const dates = [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7]];
    const ticks = [0, 5, 10, 15, 20];
    const maxVal = 20;
    const left = 34, right = 348, yTop = 12, yBase = 196;
    const slot = (right - left) / values.length;
    const barW = 32;
    const yFor = (v) => yBase - (v / maxVal) * (yBase - yTop);
    const fmt = (v) => (showLeadingZeros ? String(v).padStart(2, '0') : String(v));
    const fmtDate = ([m, d]) => `${fmt(m)}/${fmt(d)}`;
    const barPath = (x, y, w, h) => {
      const r = Math.min(4, w / 2, h);
      return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
    };
    return (
      <svg viewBox={CHART_VIEWBOX} width="100%" role="img" aria-label="Bar chart demonstrating y-axis labels" style={CHART_STYLE}>
        {ticks.map((t) => {
          const y = yFor(t);
          return (
            <g key={t}>
              <line x1={left} y1={y} x2={right} y2={y} stroke={GRID} strokeWidth="1" />
              <text x={left - 5} y={y + 3} textAnchor="end" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{`${fmt(t)}%`}</text>
            </g>
          );
        })}
        {values.map((v, i) => {
          const x = left + i * slot + (slot - barW) / 2;
          const y = yFor(v);
          return (
            <g key={i}>
              <path d={barPath(x, y, barW, yBase - y)} fill="#197f9f" />
              <text x={x + barW / 2} y={yBase + 13} textAnchor="middle" fontSize={AXIS_FONT_SIZE} fill={AXIS_TEXT} fontFamily="inherit">{fmtDate(dates[i])}</text>
            </g>
          );
        })}
        <line x1={left} y1={yBase} x2={right} y2={yBase} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
      </svg>
    );
  }

  render() {
    return (
      <div className="full-width sectionContainer">
        <SectionBanner image="/images/background-lines-gradient-purple.webp" title="Data Visualizations" />
        <SectionPageHeader description="Data visualizations are central to how we communicate insights to our clients, so visual consistency matters. Using our approved chart colors ensures accessibility and a cohesive look across all reports and dashboards. Below you'll find a set of high-level guidelines to help you make clear, effective, and on-brand chart decisions." />
        <div className="max-width-container">
          <div className="small-padding no-top-padding tiny-top-margin">
            <Link className="downloadText" to={{ pathname: '/design/colors', state: { scrollTo: 'chart-colors' } }}>
              View Chart Colors
              <span className="material-symbols-rounded">arrow_outward</span>
            </Link>
          </div>
          <div className="medium-bottom-margin">
            <div className="small-padding">
              <h3 className="primary-color-dark small-bottom-margin"><b>{'Best Practices'}</b></h3>
              <h4 className="primary-color-dark">{'Group Minor Values into "Other"'}</h4>
              <p className="tiny-top-margin">{'Too many small categories can overwhelm a chart and make it harder to interpret. Grouping less significant values into a single “Other” category reduces visual noise and helps viewers focus on the most meaningful comparisons.'}</p>
              <ul>
                <li>{'Limit the number of categories shown in a chart'}</li>
                <li>{'Avoid displaying many tiny segments that add cognitive load without insight'}</li>
                <li>{'Combine smaller or less significant values into an “Other” category'}</li>
                <li>{'Use a neutral color (e.g. gray) to de-emphasize the “Other” category'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#4CAF50')}>
                    {this.renderStackedBarChart(true)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'By grouping minor values you amplify what matters'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#ED4843')}>
                    {this.renderStackedBarChart(false)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#ED4843', fontSize: '18px', marginTop: '2px' }}>cancel</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{"Don't add small values that cause extraneous cognitive load"}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin large-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{"Don't Overcrowd Axis Labels"}</h4>
              <p className="tiny-top-margin">{"Avoid displaying too many labels on an axis — it creates visual noise and makes charts hard to read. Only show what's needed to orient the viewer."}</p>
              <ul>
                <li>{'Show the minimum number of time markers needed to understand change over time'}</li>
                  <ul><li>{'Typically 4-10 ticks per axis'}</li></ul>
                <li>{'Abbreviate where possible (e.g. Jan instead of January, $1M instead of $1,000,000)'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #4CAF50", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-11.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Abbreviated labels keep the axis clean and easy to scan.'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #ED4843", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-12.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#ED4843', fontSize: '18px', marginTop: '2px' }}>cancel</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{"Too many long labels crowd the axis and compete with the data."}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin large-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{"Evenly Space Tick Marks"}</h4>
              <p className="tiny-top-margin">{"Tick marks should always be evenly spaced to accurately represent the underlying data scale, ensuring visual consistency and preventing misinterpretation of values or trends."}</p>
              <ul>
                <li>{'Use even, predictable increments (e.g. 20, 40, 60 — not 17, 39, 62)'}</li>
                <li>{'Choose round numbers that are easy to scan mentally'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #4CAF50", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-13.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Evenly spaced, round numbers'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #ED4843", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-14.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#ED4843', fontSize: '18px', marginTop: '2px' }}>cancel</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{"Inconsistently spaced, non-standard increments"}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin large-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{'Omit Leading Zeros'}</h4>
              <p className="tiny-top-margin">{'Padding numbers with leading zeros (like 05 or 08) adds visual noise, slows scanning, and can read as a code or a different unit rather than a value — hurting both usability and clarity in data visualizations. Show the natural number instead.'}</p>
              <ul>
                <li>{'Write axis and label values without leading zeros (5, not 05)'}</li>
                <li>{'Reserve zero-padding for true fixed-width codes (like IDs), never chart scales'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#4CAF50')}>
                    {this.renderBarChart(false)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Clean numbers keep the axis easy to scan.'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#ED4843')}>
                    {this.renderBarChart(true)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#ED4843', fontSize: '18px', marginTop: '2px' }}>cancel</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Leading zeros (e.g. 05, 01/07) add clutter and slow comprehension.'}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin large-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{'Never Angle Axis Labels'}</h4>
              <p className="tiny-top-margin">{'Axis labels should always sit horizontally. Angled text slows reading: we read by sweeping along a straight, horizontal baseline, so tilting text to breaks the path the eye expects and forces the reader to decode each label as its own small task instead of taking the axis in at a glance. That effort is pure overhead — it competes with the work of actually interpreting the data, and it compounds with every label on the axis.'}</p>
              <ul>
                <li>{'Never rotate axis labels to 45° or vertical'}</li>
                <li>{'If labels don’t fit, shorten them (Jan, not January), break to a second line, show fewer ticks, or use a horizontal bar chart'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#4CAF50')}>
                    {this.renderAxisAngleChart(false)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Horizontal labels read in a single pass and line up with the bars they describe.'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#ED4843')}>
                    {this.renderAxisAngleChart(true)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#ED4843', fontSize: '18px', marginTop: '2px' }}>cancel</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Angled labels force the reader to decode each one separately, slowing chart comprehension.'}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin medium-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{'Use a Single Hue for Sequential or Time Period Data'}</h4>
              <p className="tiny-top-margin">{'Use a single color with varying intensity to represent sequential values, creating a clear and intuitive sense of progression and hierarchy.'}</p>
              <ul>
                <li>{'Use one color to represent all sequential values'}</li>
                <li>{'Vary intensity (light to dark) to show progression'}</li>
                <li>{'Use lighter shades for lower values'}</li>
                <li>{'Use darker or more saturated shades for higher values or more recent periods'}</li>
              </ul>
            </div>
            <ScrollFadeIn>
              <div className="row">
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #4CAF50", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-6.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Darker shades represent higher values, intensity signals magnitude.'}</p>
                  </div>
                </div>
                <div className="small-12 medium-6 centered-text full-height small-padding">
                  <div className="small-padding" style={chartCardStyle('#4CAF50')}>
                    {this.renderSequentialLineChart()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'Darker shades mark the most recent series.'}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
          <div className="medium-top-margin large-bottom-margin">
            <div className="small-padding">
              <h4 className="primary-color-dark">{"Use Diverging Colors Around a Midpoint"}</h4>
              <p className="tiny-top-margin">{"Use a diverging color palette when values split around a meaningful midpoint (such as zero or an average). Apply contrasting hues on either side to show direction and magnitude from that center. This makes it easy to distinguish values above vs. below the breakpoint."}</p>
            </div>
            <ScrollFadeIn>
              <div className="row" style={{justifyContent: "center"}}>
                <div className="small-12 medium-6 full-height small-padding">
                  <div className="small-padding" style={{border: "2px solid #4CAF50", borderRadius: '8px', overflow: 'hidden'}}>
                    <img src={'/design/rules-chart-10.png'} className="full-width" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '8px' }}>
                    <span className="material-symbols-rounded" style={{ color: '#4CAF50', fontSize: '18px', marginTop: '2px' }}>check_circle</span>
                    <p className="tiny-top-margin" style={{ margin: 0, fontSize: '0.85rem' }}>{'A diverging palette makes it easy to read values above or below a meaningful midpoint.'}</p>
                  </div>
                </div>
              </div>
            </ScrollFadeIn>
          </div>
        </div>
      </div>
    );
  }
}
